import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/components'
import { Button } from '@/ui/design-system'
import { Loader2, UserPlus } from '@/ui/icons'
import { TripsApi } from '@/shared/api/trips'
import type { TripFriendItem } from '@/shared/api/trips'
import { getUserProfileUrl } from '@/shared/utils/userImages'
import { useAuth } from '@/modules/auth'
import { cn } from '@/shared/utils'

interface FindFriendsModalProps {
  isOpen: boolean
  onClose: () => void
  tripId: string
  onInviteSent?: () => void
}

export function FindFriendsModal({ isOpen, onClose, tripId, onInviteSent }: FindFriendsModalProps) {
  const { getAccessToken } = useAuth()
  const [friends, setFriends] = useState<TripFriendItem[]>([])
  const [loading, setLoading] = useState(false)
  const [sendingId, setSendingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !tripId) return
    setError(null)
    setLoading(true)
    const api = new TripsApi(getAccessToken)
    api
      .getTripInviteFriends(tripId)
      .then(setFriends)
      .catch((err) => setError(err?.message ?? 'Failed to load friends'))
      .finally(() => setLoading(false))
  }, [isOpen, tripId, getAccessToken])

  async function handleInvite(friend: TripFriendItem) {
    if (friend.invite_status === 'pending') return
    setSendingId(friend.id)
    const api = new TripsApi(getAccessToken)
    try {
      await api.sendTripInvite(tripId, friend.id)
      setError(null)
      setFriends((prev) =>
        prev.map((f) => (f.id === friend.id ? { ...f, invite_status: 'pending' as const } : f))
      )
      onInviteSent?.()
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to send invite')
    } finally {
      setSendingId(null)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Find Friends</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          People you’ve been on trips with. Invite them to join this trip.
        </p>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        {!loading && friends.length === 0 && !error && (
          <p className="text-sm text-muted-foreground py-4">
            No trip friends yet. Share a trip link with someone; once they’ve joined a trip with you,
            they’ll show up here.
          </p>
        )}
        {!loading && friends.length > 0 && (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
            {friends.map((friend) => {
              const displayName =
                friend.full_name?.trim() || friend.email || 'Crew member'
              const isPending = friend.invite_status === 'pending'
              const isSending = sendingId === friend.id
              return (
                <li
                  key={friend.id}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg border p-3',
                    'bg-card text-card-foreground'
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {friend.profile_image_path ? (
                      <img
                        src={getUserProfileUrl(friend.profile_image_path)}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-muted-foreground font-medium"
                        aria-hidden
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{displayName}</p>
                      {friend.email && friend.full_name && (
                        <p className="text-xs text-muted-foreground truncate">{friend.email}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isPending || isSending}
                    onClick={() => handleInvite(friend)}
                    className="flex-shrink-0 gap-1.5"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                    {isPending ? 'Invited' : 'Invite'}
                  </Button>
                </li>
              )
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
